import classNames from 'classnames';
import { map, get, head } from 'lodash';
import PropTypes from 'prop-types';
import React from 'react';

import Accordion from 'components/Accordion';
import SizeButton from 'components/SizeButton';
import { SubBold } from 'components/Sub';
import { TYPES } from 'constants/merchandising';
import Theme from 'styles/theme';
import I18n from 'utils/I18n';
import Size from 'utils/Size';

const SIZES_PER_ROW = 5;

const MobileSizeSelector = class extends React.PureComponent {
  constructor() {
    super();
    this.state = {
      isOpen: false,
    };
  }

  handleToggle = () =>
    this.setState(prevState => ({
      isOpen: !prevState.isOpen,
    }));

  render() {
    const { sortedSizes, onSizeSelection, type } = this.props;
    const { isOpen } = this.state;

    return (
      <div className={classNames('MobileSizeSelector', { 'MobileSizeSelector--open': isOpen })}>
        <button
          type="button"
          onClick={
            sortedSizes.length === 1
              ? () => onSizeSelection(get(head(sortedSizes), 'id'), '#size-selector')
              : this.handleToggle
          }
          className="MobileSizeSelector__toggle"
        >
          {sortedSizes.length === 1 ? (
            <SubBold className="MobileSizeSelector__toggle-text--one-size">
              {I18n.localize('collectionPage.productCards.addToCart')}
            </SubBold>
          ) : (
            <SubBold className="MobileSizeSelector__toggle-text">
              {I18n.localize('collectionPage.productCards.quickAdd')}
            </SubBold>
          )}
          {!(sortedSizes.length === 1) && <i className="MobileSizeSelector__toggle-icon" />}
        </button>
        <Accordion isOpen={isOpen}>
          <ul
            className={classNames('MobileSizeSelector__grid', {
              'MobileSizeSelector__grid--2-column': type === TYPES.SOCKS,
            })}
          >
            {map(sortedSizes, ({ id, value, available }) => (
              <li
                className={classNames('MobileSizeSelector__grid-item', {
                  'MobileSizeSelector__grid-item--2-column': type === TYPES.SOCKS,
                  'MobileSizeSelector__grid-item--3-column':
                    type !== TYPES.SOCKS && !Size.isSizeButtonSquare(value),
                })}
                key={id}
              >
                <SizeButton
                  label={value}
                  available={available}
                  disabled={!available}
                  onClick={() => onSizeSelection(id, '#size-selector')}
                />
              </li>
            ))}
          </ul>
        </Accordion>
        <style jsx>
          {`
            .MobileSizeSelector {
              background-color: ${Theme.get('colors-white')};
            }

            .MobileSizeSelector__grid {
              padding: 0 ${Theme.get('spacing') * 3}px ${Theme.get('spacing') * 2.5}px;
              margin: 0 ${Theme.get('spacing') / -2}px 0;
              display: flex;
              flex-wrap: wrap;
            }

            .MobileSizeSelector__grid--2-column {
              display: grid;
              grid-template-columns: repeat(auto-fill, minmax(50%, 1fr));
            }

            .MobileSizeSelector__grid-item {
              display: inline-block;
              width: ${100 / SIZES_PER_ROW}%;
              max-width: ${Theme.get('spacing') * 7.5}px;
              padding: 0 ${Theme.get('spacing') / 2}px ${Theme.get('spacing')}px;
              margin: 0;
            }

            .MobileSizeSelector__grid--2-column .MobileSizeSelector__grid-item--2-column {
              width: 100%;
            }

            .MobileSizeSelector__grid-item--2-column {
              width: 100%;
              max-width: none;
              flex: 0 0 50%;
            }

            .MobileSizeSelector__grid-item--3-column {
              width: 100%;
              max-width: none;
              flex: 0 0 33.3333%;
            }

            .MobileSizeSelector--open .MobileSizeSelector__toggle-icon:before {
              transform: rotate(90deg);
            }

            .MobileSizeSelector__toggle.focused {
              outline: 2px solid ${Theme.get('colors-black')};
            }

            .MobileSizeSelector__toggle {
              position: relative;
              z-index: 2;
              appearance: none;
              width: 100%;
              padding: ${Theme.get('spacing') * 1.5}px;
              padding-left: ${Theme.get('spacing') * 3}px;
              cursor: pointer;
              background-color: ${Theme.get('colors-white')};
              outline: none;
              border: 0;
              border-top: solid 1px ${Theme.get('colors-black-20')};
              text-align: ${sortedSizes.length === 1 ? 'center' : 'left'};
            }

            .MobileSizeSelector__toggle-icon:before,
            .MobileSizeSelector__toggle-icon:after {
              content: '';
              position: absolute;
              border-left: 2px solid ${Theme.get('colors-black')};
              top: ${Theme.get('spacing') * 2}px;
              right: ${Theme.get('spacing') * 4}px;
              height: ${Theme.get('spacing') * 2}px;
              transition: transform ${Theme.get('transition-duration-ms')};
            }

            .MobileSizeSelector__toggle-icon:before {
              transform: rotate(0);
            }

            .MobileSizeSelector__toggle-icon:after {
              transform: rotate(90deg);
            }
          `}
        </style>
        <style jsx global>
          {`
            .MobileSizeSelector__toggle-text--one-size {
              text-align: center;
            }

            .MobileSizeSelector__grid--2-column
              .MobileSizeSelector__grid-item--2-column
              .SizeButton {
              height: 100%;
            }
          `}
        </style>
      </div>
    );
  }
};

MobileSizeSelector.propTypes = {
  sortedSizes: PropTypes.arrayOf(
    PropTypes.shape({ id: PropTypes.number, value: PropTypes.string, available: PropTypes.bool })
  ).isRequired,
  onSizeSelection: PropTypes.func.isRequired,
  type: PropTypes.string.isRequired,
};

export default MobileSizeSelector;
